import React, { useMemo, useRef } from "react";
import { Controller, useForm } from "react-hook-form";
import {
  Autocomplete,
  Box,
  Chip,
  Collapse,
  createFilterOptions,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import Star from "@mui/icons-material/Star";
import StarBorder from "@mui/icons-material/StarBorder";
import {
  getPreferredExam2Normal,
  isNormalExam2Finding,
  normalizeExam2Values,
} from "./exam2Catalog";

const filterFindings = createFilterOptions();

const filterWithWriteIn = (options, params) => {
  const filtered = filterFindings(options, params);
  const inputValue = String(params.inputValue || "").trim();
  if (
    inputValue &&
    !options.some((option) => option.toLowerCase() === inputValue.toLowerCase()) &&
    !filtered.some((option) => String(option).toLowerCase() === inputValue.toLowerCase())
  ) {
    filtered.push(inputValue);
  }
  return filtered;
};

const formatTipLine = (tip) => {
  const lookFor = tip.lookFor?.length ? tip.lookFor.join("; ") : "";
  const diagnoses = tip.diagnoses?.length ? ` (${tip.diagnoses.join(", ")})` : "";
  if (lookFor) return `${tip.examName}: ${lookFor}${diagnoses}`;
  if (tip.rationale) return `${tip.examName}: ${tip.rationale}${diagnoses}`;
  return `${tip.examName}${diagnoses}`;
};

/**
 * Exam2 systems table: multi-select freeSolo, Tab fills preferred normal, Make-default on chips.
 */
const Exam2SystemsTable = ({
  systems = [],
  onChangeSystemValue,
  tipsOpen = false,
  tipsBySystemId = {},
  sexKey = null,
  preferredBySystemId = {},
  onMakeDefaultNormal,
}) => {
  const inputRefs = useRef([]);

  const formValues = useMemo(
    () => ({
      rows: Object.fromEntries(
        (systems || []).map((system) => [system.id, normalizeExam2Values(system.value)]),
      ),
    }),
    // Sync when ids or committed values change; avoid new object identity each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      systems
        .map((system) => `${system.id}\0${normalizeExam2Values(system.value).join("\u0001")}`)
        .join("\n"),
    ],
  );

  const { control, getValues, setValue } = useForm({
    values: formValues,
  });

  const visibleSystems = useMemo(
    () => (systems || []).filter((system) => !system.hidden),
    [systems],
  );

  const focusSystemAt = (index) => {
    if (index < 0) return;
    if (index >= visibleSystems.length) {
      inputRefs.current[visibleSystems.length - 1]?.blur?.();
      return;
    }
    const node = inputRefs.current[index];
    if (node) {
      node.focus();
      if (typeof node.select === "function") node.select();
    }
  };

  const acceptPreferredAndAdvance = (system, index) => {
    const current = normalizeExam2Values(getValues(`rows.${system.id}`));
    if (current.length === 0) {
      const preferred = getPreferredExam2Normal(system, { sexKey, preferredBySystemId });
      if (preferred) {
        const next = [preferred];
        setValue(`rows.${system.id}`, next, { shouldDirty: true });
        onChangeSystemValue?.(system.id, next);
      }
    }
    focusSystemAt(index + 1);
  };

  if (!visibleSystems.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        No exam systems available.
      </Typography>
    );
  }

  return (
    <Stack spacing={2}>
      {visibleSystems.map((system, index) => {
        // Keep clinician-configured option order (Customize exam); do not re-sort A–Z.
        const options = Array.isArray(system.options) ? [...system.options] : [];
        const tips = tipsBySystemId[system.id] || [];
        const preferred = getPreferredExam2Normal(system, { sexKey, preferredBySystemId });
        return (
          <Box key={system.id}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "160px 1fr" },
                gap: 1.5,
                alignItems: { sm: "center" },
              }}
            >
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {system.name}
              </Typography>
              <Controller
                name={`rows.${system.id}`}
                control={control}
                render={({ field }) => {
                  const value = normalizeExam2Values(field.value);
                  return (
                    <Autocomplete
                      multiple
                      freeSolo
                      selectOnFocus
                      clearOnBlur
                      handleHomeEndKeys
                      filterSelectedOptions
                      options={options}
                      filterOptions={filterWithWriteIn}
                      value={value}
                      onChange={(_, next) => {
                        const list = normalizeExam2Values(
                          (Array.isArray(next) ? next : []).map((item) =>
                            typeof item === "string" ? item : String(item || ""),
                          ),
                        );
                        field.onChange(list);
                        onChangeSystemValue?.(system.id, list);
                      }}
                      onKeyDown={(event) => {
                        // Plain Tab: fill preferred normal when empty, then next system.
                        // Shift+Tab keeps browser/back focus behavior.
                        if (event.key !== "Tab" || event.shiftKey || event.ctrlKey || event.metaKey || event.altKey) {
                          return;
                        }
                        event.preventDefault();
                        event.stopPropagation();
                        acceptPreferredAndAdvance(system, index);
                      }}
                      renderTags={(tagValue, getTagProps) =>
                        tagValue.map((option, tagIndex) => {
                          const abnormal = !isNormalExam2Finding(system, option);
                          const isPreferred =
                            preferred &&
                            preferred.toLowerCase() === String(option).toLowerCase();
                          const { key, ...tagProps } = getTagProps({ index: tagIndex });
                          return (
                            <Chip
                              {...tagProps}
                              key={key}
                              label={option}
                              size="small"
                              color={abnormal ? "error" : "default"}
                              variant={abnormal ? "outlined" : "filled"}
                              icon={
                                <Tooltip
                                  title={
                                    isPreferred
                                      ? "Default normal for Tab"
                                      : "Make this the default normal"
                                  }
                                >
                                  <IconButton
                                    size="small"
                                    aria-label={
                                      isPreferred
                                        ? "Default normal finding"
                                        : "Make this the default normal"
                                    }
                                    onMouseDown={(event) => event.preventDefault()}
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      if (!isPreferred) {
                                        onMakeDefaultNormal?.(system.id, option);
                                      }
                                    }}
                                    sx={{ p: 0.25, ml: 0.25 }}
                                  >
                                    {isPreferred ? (
                                      <Star fontSize="inherit" color="primary" />
                                    ) : (
                                      <StarBorder fontSize="inherit" />
                                    )}
                                  </IconButton>
                                </Tooltip>
                              }
                            />
                          );
                        })
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder={
                            value.length
                              ? "Add another finding"
                              : preferred
                                ? `Tab for “${preferred}”`
                                : "Choose or type findings"
                          }
                          fullWidth
                          inputRef={(node) => {
                            inputRefs.current[index] = node;
                            if (typeof params.inputRef === "function") params.inputRef(node);
                            else if (params.inputRef) params.inputRef.current = node;
                          }}
                        />
                      )}
                    />
                  );
                }}
              />
            </Box>
            <Collapse in={tipsOpen && tips.length > 0} unmountOnExit>
              <Stack spacing={0.5} sx={{ mt: 1, pl: { sm: "176px" } }}>
                {tips.map((tip) => (
                  <Typography
                    key={`${system.id}-${tip.examName}`}
                    variant="body2"
                    color="text.secondary"
                  >
                    {formatTipLine(tip)}
                  </Typography>
                ))}
              </Stack>
            </Collapse>
          </Box>
        );
      })}
    </Stack>
  );
};

export default Exam2SystemsTable;
